import {
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { MatSelectModule } from "@angular/material/select";
import { MatToolbarModule } from "@angular/material/toolbar";
import { NavigationEnd, Router } from "@angular/router";

import { filter, map, startWith } from "rxjs";

import { AssignableUser } from "@juristech/contracts/orders/board.types";

import { OrderQuickSearchComponent } from "../../features/orders/quick-search/order-quick-search.component";
import { AuthService } from "../services/auth.service";
import { OrdersService } from "../services/orders.service";
import { ThemeService } from "../services/theme.service";
import {
  DashboardFiltersStore,
  DashboardOwnerToken,
  OWNER_UNASSIGNED,
} from "../state/dashboard-filters.store";
import {
  OrderOwnerChangeEvent,
  OrderOwnershipStore,
} from "../state/order-ownership.store";

@Component({
  selector: "admin-header",
  standalone: true,
  imports: [
    FormsModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatFormFieldModule,
    MatSelectModule,
    OrderQuickSearchComponent,
  ],
  template: `
    <mat-toolbar color="primary">
      <div class="header-inner">
        @if (showNavToggle()) {
          <button
            mat-icon-button
            type="button"
            class="header-nav-toggle"
            aria-label="Show navigation"
            (click)="toggleSidenav.emit()"
          >
            <mat-icon>menu</mat-icon>
          </button>
        }

        <admin-order-quick-search
          class="header-quick-search"
          [currentOrderId]="currentOrderId()"
        />

        <mat-form-field
          appearance="outline"
          class="header-assigned-filter"
          subscriptSizing="dynamic"
        >
          <mat-label>Assigned to</mat-label>
          <mat-select
            multiple
            [ngModel]="dropdownValue()"
            (ngModelChange)="onAssignedToChange($event)"
          >
            <mat-option [value]="unassignedSentinel">Unassigned</mat-option>
            @for (u of teammateOptions(); track u.userId) {
              <mat-option [value]="u.userId">{{ u.name }}</mat-option>
            }
          </mat-select>
        </mat-form-field>

        <span class="spacer"></span>

        @if (currentUserId() !== null) {
          <button
            type="button"
            class="my-orders-pill"
            [class.my-orders-pill--active]="myOrdersActive()"
            [attr.aria-pressed]="myOrdersActive()"
            (click)="onMyOrdersToggle()"
          >
            <mat-icon>person</mat-icon>
            <span>My orders</span>
            <span class="my-orders-pill__count">{{ ownedCount() }}</span>
          </button>
        }

        <button
          mat-icon-button
          type="button"
          [attr.aria-label]="
            isDarkTheme() ? 'Switch to light theme' : 'Switch to dark theme'
          "
          (click)="onThemeToggle()"
        >
          <mat-icon>{{ isDarkTheme() ? "light_mode" : "dark_mode" }}</mat-icon>
        </button>

        <button
          mat-icon-button
          aria-label="Account menu"
          [matMenuTriggerFor]="userMenu"
        >
          <mat-icon>account_circle</mat-icon>
        </button>
      </div>
      <mat-menu #userMenu="matMenu">
        <button mat-menu-item (click)="goToProfile()">
          <mat-icon>person</mat-icon>
          <span>Profile</span>
        </button>
        <button mat-menu-item (click)="onUseSystemTheme()">
          <mat-icon>brightness_auto</mat-icon>
          <span>Use system theme</span>
        </button>
        <button mat-menu-item (click)="onLogout()">
          <mat-icon>logout</mat-icon>
          <span>Logout</span>
        </button>
      </mat-menu>
    </mat-toolbar>
  `,
  styles: [
    `
      mat-toolbar {
        border-bottom: 1px solid var(--mat-sys-outline-variant);
        padding: 12px 24px;
        height: auto;
        min-height: 0;
      }
      .header-inner {
        display: flex;
        align-items: center;
        width: 100%;
        max-width: 1400px;
        margin: 0 auto;
        padding: 0 24px;
        box-sizing: border-box;
      }
      .spacer {
        flex: 1 1 auto;
      }
      .header-nav-toggle {
        margin-right: 8px;
        color: var(--mat-sys-on-surface-variant);
      }
      .header-quick-search {
        width: min(540px, 60vw);
      }
      .header-assigned-filter {
        width: 220px;
        margin-left: 16px;
      }
      .my-orders-pill {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        margin-right: 8px;
        padding: 6px 14px;
        border: 1px solid var(--mat-sys-outline);
        border-radius: 999px;
        background: var(--mat-sys-surface);
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
        color: var(--mat-sys-on-surface);
      }
      .my-orders-pill mat-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;
      }
      .my-orders-pill__count {
        background: var(--mat-sys-surface-container-highest);
        border-radius: 999px;
        padding: 0 8px;
        font-size: 12px;
        line-height: 18px;
      }
      .my-orders-pill--active {
        background: var(--mat-sys-primary);
        border-color: var(--mat-sys-primary);
        color: var(--mat-sys-on-primary);
      }
      .my-orders-pill--active .my-orders-pill__count {
        background: color-mix(
          in srgb,
          var(--mat-sys-on-primary) 25%,
          transparent
        );
      }
    `,
  ],
})
export class HeaderComponent implements OnInit {
  private authService = inject(AuthService);
  private router = inject(Router);
  private readonly ordersService = inject(OrdersService);
  private readonly filtersStore = inject(DashboardFiltersStore);
  private readonly ownershipStore = inject(OrderOwnershipStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly themeService = inject(ThemeService);

  /**
   * Whether to render the nav hamburger — the shell passes `true` only while
   * the drawer is closed, so the header affordance exists solely to bring the
   * nav back (the drawer's own toggle handles hiding).
   */
  showNavToggle = input(true);

  /** Asks the shell to open/close the side navigation drawer. */
  toggleSidenav = output();

  /** Exposed to the template for the "Assigned to → Unassigned" option value. */
  readonly unassignedSentinel = OWNER_UNASSIGNED;

  /** Effective dark-mode state — drives the toggle's icon and aria-label. */
  readonly isDarkTheme = this.themeService.isDark;

  private readonly assignableUsers = signal<AssignableUser[]>([]);

  /** The current user's owned-order count, shown on the "My orders" pill. */
  readonly ownedCount = signal(0);

  /** The current admin's user id (JWT `sub`), or `null` if unauthenticated. */
  readonly currentUserId = computed<number | null>(
    () => this.authService.currentUser?.id ?? null,
  );

  /**
   * Whether the active owner filter includes the current user — the board's "My
   * orders" pill. Mutually exclusive with the "Assigned to" dropdown by
   * construction: the pill writes `[currentUserId]` and the dropdown writes a set
   * drawn from {teammate ids, 'unassigned'} (never the current user), so the two
   * sets never mix.
   */
  readonly myOrdersActive = computed(() => {
    const id = this.currentUserId();
    return id !== null && this.filtersStore.owner().includes(id);
  });

  /**
   * The value bound to the multi-select "Assigned to" dropdown — the owner list
   * with the current user's id removed. Empty while "My orders" owns the filter,
   * so the pill and the dropdown never both look active.
   */
  readonly dropdownValue = computed<DashboardOwnerToken[]>(() => {
    const id = this.currentUserId();
    return this.filtersStore.owner().filter((token) => token !== id);
  });

  /** Teammate options for the "Assigned to" dropdown, excluding the current user (the "My orders" pill owns "mine"). */
  readonly teammateOptions = computed(() => {
    const id = this.currentUserId();
    return this.assignableUsers().filter((u) => u.userId !== id);
  });

  /** Surfaces the `/orders/:id` parameter from the current URL so the
   *  quick-search widget can dim and disable the active order's row.
   *  Falls back to 0 (no row disabled) on every other route. */
  readonly currentOrderId = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.extractOrderIdFromUrl(this.router.url)),
      startWith(this.extractOrderIdFromUrl(this.router.url)),
    ),
    { initialValue: this.extractOrderIdFromUrl(this.router.url) },
  );

  ngOnInit(): void {
    this.ordersService
      .getAssignableUsers()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (list) => this.assignableUsers.set(list),
      });

    const id = this.currentUserId();
    if (id !== null) {
      this.ordersService
        .getOwnedCount(id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res) => this.ownedCount.set(res.count),
        });
    }

    // Keep the pill count live: every order (un)assigned to/from the current user
    // shifts it by one, so we never go stale until the next reload.
    this.ownershipStore.ownerChanges$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => this.applyOwnershipChange(event));
  }

  /**
   * Applies an "Assigned to" multi-select selection. Writes the chosen set of
   * teammate ids and/or the `unassigned` sentinel to the shared URL-backed filter
   * store; the board reloads reactively off the `owner` param. The set never
   * contains the current user's id (excluded from the options), so writing it
   * clears the mutually-exclusive "My orders" pill.
   *
   * @param value - The selected owner tokens: any mix of teammate ids and the
   *   `unassigned` sentinel. An empty array means "Anyone" (no owner filter).
   */
  onAssignedToChange(value: DashboardOwnerToken[]): void {
    this.filtersStore.setOwner(value);
  }

  /**
   * Toggles the "My orders" pill. Activating it sets the owner filter to exactly
   * `[currentUserId]` (clearing any dropdown picks); deactivating it clears the
   * filter. Mutually exclusive with the "Assigned to" dropdown by construction —
   * both write the shared `owner` list.
   */
  onMyOrdersToggle(): void {
    const id = this.currentUserId();
    if (id === null) return;
    this.filtersStore.setOwner(this.myOrdersActive() ? [] : [id]);
  }

  /**
   * Adjusts the "My orders" count by the delta implied by an ownership change:
   * +1 when an order becomes mine, −1 when one stops being mine, 0 otherwise
   * (clamped at 0 as a guard against a missed/duplicated event).
   */
  private applyOwnershipChange(event: OrderOwnerChangeEvent): void {
    const id = this.currentUserId();
    if (id === null) return;
    const gained = event.newOwnerId === id && event.previousOwnerId !== id;
    const lost = event.previousOwnerId === id && event.newOwnerId !== id;
    if (gained) {
      this.ownedCount.update((count) => count + 1);
    } else if (lost) {
      this.ownedCount.update((count) => Math.max(0, count - 1));
    }
  }

  /** Flips light↔dark and persists the choice as an explicit preference. */
  onThemeToggle(): void {
    this.themeService.toggle();
  }

  /** Drops the explicit theme preference and re-follows the OS setting. */
  onUseSystemTheme(): void {
    this.themeService.useSystem();
  }

  goToProfile(): void {
    this.router.navigate(["/profile"]);
  }

  onLogout(): void {
    this.authService.logout();
  }

  private extractOrderIdFromUrl(url: string): number {
    const match = /\/orders\/(\d+)(?:[/?#]|$)/.exec(url);
    if (!match) return 0;
    const parsed = Number(match[1]);
    return Number.isFinite(parsed) ? parsed : 0;
  }
}
